// Gate D2.4, audio and music (engine/docs/spikes/002-green-valley/brief.md), on @rpg/audio. One engine for the page,
// mounted once in main.ts on whatever scene runs:
// - one AudioContext: the boot's, created on the first gesture (the title's start); the engine attaches to it and never
//   makes its own. The context is resumed on a later press if the browser left it suspended, and suspended while the
//   tab is hidden;
// - the impact router's sound channel (phase B6's stub) plays its cue at the hit point; `audio:cue` events play any
//   cue (an important one sends a caption); swings, the dodge and the block on `combat:move`; a growl when an enemy
//   turns alert; footsteps on the Wanderer's foot plants, by the world's surface under the foot; gathering, breaking
//   from the things' log, pickups on `item:gained`; the level-up chime on `progress:level`; UI ticks on presses and focus moves;
//   babble on `dialogue:line`, ducking the rest;
// - ambience beds and spots by the region under the listener, the sky's time mark and its rain; the brook as loop
//   emitters along its course (the nearest few heard); muffling behind buildings by a ray on the solid layer;
// - the music: the mood by place and time (explore, town, night), the combat layer while an enemy is alert, the
//   victory phrase on the bar line when a fight is won; since the audio units (2026-10-20) through
//   @rpg-unit/adaptive-music's conductor over the engine's director (a music source, @rpg/contracts), on the engine's
//   clock, with an intensity dial the alert enemies turn.
// Planted defects: `audio-second-context` (a cutscene's score makes its own AudioContext: the one-context check
// must go red), `audio-limiter-bypassed` and `audio-no-ducking` (the engine's: the offline level checks go red),
// `important-sound-no-caption` (the growl keeps its important flag but loses its caption: the caption count goes red);
// the music pass's (the engine's): `music-no-reverb` (the offline reverb-tail check goes red), `music-room-unnormalised`
// (the offline headroom check goes red), and the director's
// `music-no-voice-leading`, `music-no-motif`, `music-stale-chord`, `music-unseeded`, `music-all-at-once`,
// `music-flat`, `music-no-multisample` (the table checks in tools/audio.test.ts and the package's unit tests go red).
import * as THREE from "three";
import { createAudio, INSTRUMENT_FAULTS, MIXER_FAULTS, MUSIC_FAULTS, REVERB_FAULTS, type Audio, type CaptionEvent, type Vec3 } from "@rpg/audio";
import type { EventBus } from "@rpg/kernel";
import type { Runtime } from "@rpg/runtime";
import type { SpikeBoot } from "../boot";
import type { SpikeContent } from "../content";
import { directorMusic } from "@rpg-unit/adaptive-music";
import type { SfxPreset } from "@rpg-unit/sfx-kits";
import type { GameEvents } from "../core";
import type { FaultGroup } from "../faults";
import { setImpactSound } from "../feedback";
import type { SimMount } from "../sim";
import { audioOptions, audioRows } from "./content";
import { playCutscene, type CutsceneResult } from "./cutscene";
import { offlineScenario, type OfflineName } from "./offline";

import { AUDIO_FAULT_NAMES, AUDIO_FAULTS, UNCAPTIONED_CUE } from "./faults";

export { AUDIO_FAULT_NAMES, AUDIO_FAULTS, UNCAPTIONED_CUE } from "./faults";

/** The engine's music switches by their `?faults=` names. */
const MUSIC_SWITCH: Record<string, { get(): boolean; set(on: boolean): void }> = {
  "music-no-voice-leading": { get: () => MUSIC_FAULTS.noVoiceLeading, set: (on) => void (MUSIC_FAULTS.noVoiceLeading = on) },
  "music-no-motif": { get: () => MUSIC_FAULTS.noMotif, set: (on) => void (MUSIC_FAULTS.noMotif = on) },
  "music-stale-chord": { get: () => MUSIC_FAULTS.staleChord, set: (on) => void (MUSIC_FAULTS.staleChord = on) },
  "music-unseeded": { get: () => MUSIC_FAULTS.unseeded, set: (on) => void (MUSIC_FAULTS.unseeded = on) },
  "music-all-at-once": { get: () => MUSIC_FAULTS.allAtOnce, set: (on) => void (MUSIC_FAULTS.allAtOnce = on) },
  "music-flat": { get: () => MUSIC_FAULTS.flat, set: (on) => void (MUSIC_FAULTS.flat = on) },
  "music-no-multisample": { get: () => INSTRUMENT_FAULTS.firstRootOnly, set: (on) => void (INSTRUMENT_FAULTS.firstRootOnly = on) },
  "music-no-reverb": { get: () => MIXER_FAULTS.noReverb, set: (on) => void (MIXER_FAULTS.noReverb = on) },
  "music-room-unnormalised": { get: () => REVERB_FAULTS.unnormalised, set: (on) => void (REVERB_FAULTS.unnormalised = on) },
};

const readSwitches = (search: string): void => {
  const on = (new URLSearchParams(search).get("faults") ?? "").split(",");
  if (on.includes("audio-second-context")) AUDIO_FAULTS.secondContext = true;
  if (on.includes("important-sound-no-caption")) AUDIO_FAULTS.noCaption = true;
  if (on.includes("audio-limiter-bypassed")) MIXER_FAULTS.limiterBypassed = true;
  if (on.includes("audio-no-ducking")) MIXER_FAULTS.noDucking = true;
  for (const [name, sw] of Object.entries(MUSIC_SWITCH)) if (on.includes(name)) sw.set(true);
};

/** What the audio needs from the page. */
export interface AudioMountDeps {
  runtime: Runtime;
  content: SpikeContent;
  /** The kernel game's bus (combat, sky, dialogue, audio:cue). */
  events: EventBus<GameEvents>;
  boot: SpikeBoot;
  /** The listener (the camera that draws the frame). */
  camera: () => THREE.Camera;
  sim: SimMount;
  setting: (id: string) => unknown;
  onSettings: (fn: () => void) => void;
  tier: string;
  /** Whether the game is in play (no screen over it). */
  playing: () => boolean;
}

/** The mounted audio. */
export interface SpikeAudio {
  engine: Audio;
  /** Every caption event so far (the last 100). */
  captions(): CaptionEvent[];
  faultGroup: FaultGroup;
  handle: ReturnType<typeof audioHandle>;
}

type V3 = [number, number, number];
const vec = (p: { x: number; y: number; z: number } | readonly number[]): V3 => (Array.isArray(p) ? [p[0]!, p[1]!, p[2]!] : [(p as { x: number }).x, (p as { y: number }).y, (p as { z: number }).z]);

/** Mount the page's audio. */
export function mountAudio(d: AudioMountDeps): SpikeAudio {
  if (typeof location !== "undefined") readSwitches(location.search);
  const { mix } = audioRows(d.content);
  const opts = audioOptions(d.content, d.tier, { strip: AUDIO_FAULTS.noCaption ? UNCAPTIONED_CUE : null });
  const realCaption = (d.content.registry.get("sfx", UNCAPTIONED_CUE) as SfxPreset | undefined)?.caption?.id ?? null;
  const occlusion = d.sim.occlusion;
  const engine = createAudio({ ...opts, deferLoops: true, ...(occlusion ? { occlusion: (a, b) => occlusion(a, b) } : {}) });
  // the music's conductor (@rpg-unit/adaptive-music): it asks the engine's director, on the engine's clock, for the
  // mood by place, the fight's states and the intensity; the engine schedules the director itself
  const music = directorMusic({ director: engine.music.director()!, table: opts.music!.table, clock: () => engine.now(), moods: mix.moods });
  // the warm-up after the context exists, a few milliseconds a frame (a bed's render is long: it waits its turn),
  // the UI and the effects first, then the music's instruments (the music starts once they're ready), then the beds
  const BUS_ORDER = ["ui", "effects", "voice", "instruments", "ambience", "music"];
  const busOf = (id: string): string => (id.startsWith("inst:") ? "instruments" : opts.sounds[id]!.bus);
  const warmOrder = [...Object.keys(opts.sounds), ...Object.keys(opts.music?.instruments ?? {}).map((i) => `inst:${i}`)].sort((a, b) => BUS_ORDER.indexOf(busOf(a)) - BUS_ORDER.indexOf(busOf(b)));
  let warmLeft = warmOrder.length;
  const captionLog: CaptionEvent[] = [];
  engine.onCaption((c) => {
    captionLog.push(c);
    if (captionLog.length > 100) captionLog.shift();
  });

  // one context: the boot's (made on the first gesture); a later press resumes it if the browser left it suspended
  d.boot.onAudioContext((ctx) => {
    engine.attach(ctx);
    applyVolumes();
  });
  const resume = (): void => {
    const ctx = d.boot.audioContext();
    if (ctx && ctx.state === "suspended" && !document.hidden) void ctx.resume().catch(() => undefined);
  };
  for (const type of ["pointerdown", "keydown", "touchend"] as const) addEventListener(type, resume, { capture: true });
  document.addEventListener("visibilitychange", () => {
    const ctx = d.boot.audioContext();
    if (!ctx) return;
    if (document.hidden) void ctx.suspend().catch(() => undefined);
    else resume();
  });

  // the player's volumes
  const num = (id: string, fallback: number): number => {
    const v = d.setting(id);
    return typeof v === "number" ? v : fallback;
  };
  const applyVolumes = (): void => {
    engine.volume("master", num("audio.master", 0.8));
    engine.volume("music", num("audio.music", 0.7));
    engine.volume("effects", num("audio.effects", 0.8));
    engine.volume("ui", num("audio.effects", 0.8));
    engine.volume("ambience", num("audio.ambience", 0.7));
    engine.volume("voice", num("audio.voice", 0.8));
  };
  d.onSettings(applyVolumes);
  applyVolumes();

  // the UI: a tick on a press, a softer one when focus moves between a screen's items
  let lastMove = 0;
  const inUi = (e: Event, sel: string): boolean => !!(e.target as Element | null)?.closest?.(sel);
  document.addEventListener("click", (e) => void (inUi(e, ".rpg-ui .rpg-btn, .rpg-ui [role=switch], .rpg-ui [role=radio]") && engine.play("ui.tick")), true);
  document.addEventListener(
    "focusin",
    (e) => {
      if (!inUi(e, ".rpg-ui")) return;
      const now = performance.now();
      if (now - lastMove > 50) {
        lastMove = now;
        engine.play("ui.move");
      }
    },
    true,
  );

  // the impact router's sound channel: the cue at the hit point
  setImpactSound((cue, e) => void engine.play(cue, { pos: e.point }));

  // fighters: positions for swings, alerts for growls and the combat layer, a won fight for the victory phrase
  let fighters: { id: number; pos: V3; alive: boolean; level: string | null }[] = [];
  const posOf = (id: number): V3 | null => fighters.find((f) => f.id === id)?.pos ?? null;
  const levels = new Map<number, string | null>();
  let engagedBefore = false;
  let defeatsInFight = 0;
  const heroId = (): number | null => fighters.find((f) => f.level === null)?.id ?? null;
  // the babble voice by speaker: the npcs table's voice column (one source: a voices row), else a voices row of the
  // speaker's own id, else the default
  const npcVoice = new Map((d.content.registry.rows("npcs") as readonly { id: string; voice: { id: string } }[]).map((n) => [n.id, n.voice.id]));
  const voiceOf = (speaker: string): string => npcVoice.get(speaker) ?? (opts.voices?.[speaker] ? speaker : "default");
  const offs: (() => void)[] = [];
  offs.push(
    d.events.on("combat:move", (m) => {
      if (m.phase !== "active") return;
      const cue = m.move === "dodge" ? "dodge" : m.move === "block" ? "block" : opts.sounds[`swing.${m.weapon}`] ? `swing.${m.weapon}` : m.weapon ? "swing.staff" : null;
      if (cue) engine.play(cue, { pos: posOf(m.entity) });
    }),
    d.events.on("combat:defeated", (x) => {
      if (x.entity !== heroId()) defeatsInFight++;
    }),
    d.events.on("audio:cue", (c) => void engine.play(c.cue, { pos: c.pos, important: c.important })),
    // a townsperson babbles in the voices row its npcs row names (phase D joined); the narrator's lines (a cutscene's)
    // are read, not babbled: subtitles only (integrator)
    d.events.on("dialogue:line", (l) => void (l.speaker === "narrator" ? null : engine.say({ speaker: l.speaker, voice: voiceOf(l.speaker), text: l.text, seconds: l.seconds }))),
    // a cutscene's score: its sting as the scene starts, through this engine (cutscene.ts)
    d.events.on("story:scene", () => void playCutscene({ engine, boot: d.boot, secondContext: AUDIO_FAULTS.secondContext })),
    d.events.on("progress:level", () => void engine.play("levelup")),
    // one pickup cue (integrator): `item:gained` is the only source of it; a gathered yield has its own gather cue
    // (below, at the node), so its `item:gained` plays nothing more
    d.events.on("item:gained", (e) => void (e.source === "gather" ? null : engine.play("pickup"))),
  );

  // the brook as loop emitters along its course, and the pond's middle (the world's water shapes)
  const world = d.sim.handle.world;
  if (world) {
    let n = 0;
    for (const w of world.water() as { kind: string; points?: { x: number; z: number; surface: number }[]; x?: number; z?: number; surface?: number }[]) {
      if (w.kind === "stream" && w.points) {
        let last: { x: number; z: number } | null = null;
        for (const p of w.points) {
          if (last && Math.hypot(p.x - last.x, p.z - last.z) < 14) continue;
          engine.loop(`brook-${n++}`, "amb.brook", [p.x, p.surface, p.z]);
          last = p;
        }
      } else if (w.x !== undefined && w.z !== undefined) engine.loop(`pond-${n++}`, "amb.brook", [w.x, w.surface ?? 0, w.z], { gain: -4 });
    }
  }

  // footsteps from the Wanderer's foot plants
  let plants: number[] = [];
  let steps = 0;
  const footsteps = (): void => {
    const rep = d.sim.handle.wanderer?.report();
    if (!rep) return;
    rep.feet.forEach((f, i) => {
      const before = plants[i] ?? f.plants;
      if (f.plants > before) {
        const tip = vec(f.tip as unknown as { x: number; y: number; z: number });
        // the surface under the foot: a floor or a bridge's deck answers a foot on it (improvement step 3), else the ground
        const surface = world ? (world.surfaceAt(tip[0], tip[2], tip[1]) as string) : "grass";
        const wet = world ? world.waterAt(tip[0], tip[2]) : null;
        const cue = wet !== null && wet > tip[1] + 0.05 ? mix.footsteps.water : (mix.footsteps[surface] ?? mix.footsteps.default);
        if (cue) {
          engine.play(cue, { pos: tip });
          steps++;
        }
      }
      plants[i] = f.plants;
    });
  };

  // things: gathering, breaking, pickups (the things' event log, read as it grows)
  const seen = new WeakSet<object>();
  const things = (): void => {
    const t = d.sim.things?.();
    if (!t) return;
    for (const e of t.events.slice(-8)) {
      if (seen.has(e)) continue;
      seen.add(e);
      if (e.type === "gathered") {
        const kind = t.placement(e.id).kind as string;
        engine.play(kind.includes("ore") || e.id.includes("ore") || e.id.includes("vein") ? "gather.ore" : "gather.herb", { pos: t.at(e.id) });
      } else if (e.type === "broken") engine.play(e.id.includes("pot") ? "break.pot" : "break.crate", { pos: t.at(e.id) });
      // a pickup collected plays on its `item:gained` (one cue, above)
    }
  };

  // the place, the time, the rain: ambience and the music's mood
  let place = { area: null as string | null, time: "day" as string | null, rain: 0, region: null as string | null };
  const sense = (lis: V3): void => {
    if (world) {
      // the area is where the Wanderer stands (the camera trails behind, sometimes over a region's edge)
      const hero = fighters.find((x) => x.level === null)?.pos ?? (d.sim.fighters?.().find((x) => x.level === null)?.pos ?? lis);
      const region = world.regionAt(hero[0], hero[2]) as string | null;
      const sky = world.sky.state() as { period: string; blend: { mix: Record<string, number> } };
      place = { area: region ? (mix.areas[region] ?? region) : null, time: sky.period, rain: sky.blend.mix.rain ?? 0, region };
    }
    engine.ambience({ area: place.area, time: place.time, rain: place.rain });
    music.place({ area: place.area, time: place.time });
  };
  offs.push(d.events.on("sky:time", (m) => (place = { ...place, time: m.mark })));

  // the frame: the listener, then everything that polls
  const p = new THREE.Vector3();
  const f = new THREE.Vector3();
  let frame = 0;
  let heard: { pos: V3; forward: V3 } = { pos: [0, 0, 0], forward: [0, 0, -1] };
  let sinceSense = Infinity;
  let sinceFighters = Infinity;
  offs.push(
    d.runtime.on(
      "late",
      (c) => {
        frame++;
        sinceSense += c.realDt;
        sinceFighters += c.realDt;
        if (warmLeft > 0 && engine.context) warmLeft = engine.warmStep(4, warmOrder);
        const cam = d.camera();
        cam.getWorldPosition(p);
        cam.getWorldDirection(f);
        const lis: V3 = [p.x, p.y, p.z];
        engine.listener(lis, [f.x, f.y, f.z]);
        heard = { pos: lis, forward: [f.x, f.y, f.z] };
        footsteps();
        things();
        // fighters ten times a second, the place four times (on real time: a tool's long advance is one frame)
        if (sinceFighters >= 0.1 && d.sim.fighters) {
          sinceFighters = 0;
          fighters = d.sim.fighters();
          for (const x of fighters) {
            if (x.level === null) continue;
            const was = levels.get(x.id);
            if (x.alive && x.level === "alert" && was !== undefined && was !== "alert") engine.play("enemy.growl", { pos: x.pos });
            levels.set(x.id, x.alive ? x.level : null);
          }
        }
        // the fight, through the adaptive music feature: engaged is its combat state (the layer on the bar), leaving a
        // won fight its victory (on the bar line); the intensity dial follows how many enemies are alert (in the combat
        // state the director's layer is already in, so the dial changes nothing heard: it is the seam an encounter
        // director would drive)
        const engaged = d.sim.engaged?.() ?? false;
        if (engaged !== engagedBefore) {
          music.fight({ engaged, won: defeatsInFight > 0 });
          if (engaged) defeatsInFight = 0;
          engagedBefore = engaged;
        }
        music.intensity(engaged ? Math.min(1, fighters.filter((x) => x.level === "alert" && x.alive).length / 3) : 0);
        if (sinceSense >= 0.25) {
          sinceSense = 0;
          sense(lis);
        }
        engine.update();
      },
      { name: "audio: listener and polling", order: 50 },
    ),
  );

  const faultGroup: FaultGroup = {
    names: AUDIO_FAULT_NAMES,
    get: (n) => (n === "audio-second-context" ? AUDIO_FAULTS.secondContext : n === "important-sound-no-caption" ? AUDIO_FAULTS.noCaption : n === "audio-limiter-bypassed" ? MIXER_FAULTS.limiterBypassed : n === "audio-no-ducking" ? MIXER_FAULTS.noDucking : (MUSIC_SWITCH[n]?.get() ?? false)),
    set: (n, on) => {
      MUSIC_SWITCH[n]?.set(on);
      if (n === "audio-second-context") AUDIO_FAULTS.secondContext = on;
      if (n === "important-sound-no-caption") {
        AUDIO_FAULTS.noCaption = on;
        const s = opts.sounds[UNCAPTIONED_CUE];
        if (s) s.caption = on ? null : realCaption;
      }
      if (n === "audio-limiter-bypassed") MIXER_FAULTS.limiterBypassed = on;
      if (n === "audio-no-ducking") MIXER_FAULTS.noDucking = on;
    },
  };

  const self: SpikeAudio = {
    engine,
    captions: () => [...captionLog],
    faultGroup,
    handle: audioHandle({ engine, content: d.content, tier: d.tier, captions: () => captionLog, place: () => ({ ...place }), listener: () => ({ pos: [...heard.pos], forward: [...heard.forward] }), steps: () => steps, conductor: () => music.state(), cutscene: () => playCutscene({ engine, boot: d.boot, secondContext: AUDIO_FAULTS.secondContext }) }),
  };
  return self;
}

/** The `__rpg.audio` keys. */
function audioHandle(h: { engine: Audio; content: SpikeContent; tier: string; captions: () => CaptionEvent[]; place: () => unknown; listener: () => { pos: number[]; forward: number[] }; steps: () => number; conductor: () => unknown; cutscene: () => CutsceneResult }) {
  const { engine } = h;
  return {
    /** The engine's numbers: plays and captions by cue, voices, loops, ducking, babble, the music's state, the
     *  ambience, the context's state. */
    stats: () => engine.stats(),
    /** Problems the engine reported (a second context refused). */
    problems: () => engine.problems(),
    /** Play a cue (at a world point, or on the listener). */
    play: (cue: string, pos?: Vec3 | null, important?: boolean) => engine.play(cue, { pos: pos ?? null, ...(important ? { important } : {}) }),
    /** Babble a line (as `dialogue:line` does). */
    say: (speaker: string, text: string, seconds = 3) => engine.say({ speaker, text, seconds }),
    /** The captions sent so far. */
    captions: () => h.captions().map((c) => ({ ...c })),
    /** The music's log (moods, combat in and out, victory, each on its bar line), its bar grid, and the music source's
     *  state as the adaptive music conductor sees it (mood, state, intensity, grid, the place it last asked for). */
    music: () => {
      const dir = engine.music.director();
      return dir ? { state: dir.state(), log: dir.log.map((e) => ({ ...e })), bars: [...dir.bars], source: h.conductor() } : null;
    },
    /** The listener's position and facing last frame. */
    listener: h.listener,
    /** Where the ambience thinks the listener is. */
    place: h.place,
    /** Footsteps played. */
    steps: h.steps,
    /** The live output's peak (linear) over the analyser's last block. */
    meter: () => engine.meter(),
    /** Play a cutscene's score as a scene starting does (story:scene). */
    cutscene: () => h.cutscene(),
    /** Render a scenario on an OfflineAudioContext with this page's content and read its numbers (gate D2.4). */
    offline: (name: OfflineName) => offlineScenario(name, h.content, h.tier),
  };
}
